import apiClient from './apiClient';
import {
  SongListParams,
  SongListResponse,
  SongDetailsParams,
  AudioUrlResponse,
  CoverArtUrlResponse,
} from '@/types/api';
import { SongDetails } from '@/types/song';
import { appDataStore } from '@/services/cache/appDataStore';

export const songsApi = {
  async listSongs(params: SongListParams = {}): Promise<SongListResponse> {
    // Use cached data instead of API call
    const allSongs = appDataStore.getAllSongs();

    // Apply search filter if provided
    let filteredSongs = allSongs;
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filteredSongs = allSongs.filter(song =>
        song.songName.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting - default to lastUpdated (most recently modified first)
    const sortField = params.sort || 'lastUpdated';
    const sortOrder = params.order || 'desc';
    filteredSongs.sort((a, b) => {
      const aVal = a[sortField as keyof typeof a] || '';
      const bVal = b[sortField as keyof typeof b] || '';
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    // Apply pagination
    const limit = params.limit || 50;
    const offset = params.offset || 0;
    const paginatedSongs = filteredSongs.slice(offset, offset + limit);

    return {
      songs: paginatedSongs,
      pagination: {
        total: filteredSongs.length,
        limit,
        offset,
        hasNext: offset + limit < filteredSongs.length,
      },
    };
  },

  async getSongDetails(
    songId: number,
    params: SongDetailsParams = { includeRevisions: true, includeComments: true }
  ): Promise<SongDetails> {
    // Lazily load this song's revisions/comments, then read from cache.
    await appDataStore.ensureSongDetails(songId);

    const song = appDataStore.getSong(songId);
    if (!song) {
      throw new Error(`Song not found: ${songId}`);
    }

    const revisions = params.includeRevisions ? appDataStore.getRevisions(songId) : [];
    const comments = params.includeComments ? appDataStore.getComments(songId) : [];

    return {
      song,
      revisions,
      comments,
    };
  },

  async getAudioUrl(
    songId: number,
    params?: { revisionId?: number; expiration?: number }
  ): Promise<AudioUrlResponse> {
    const response = await apiClient.get(`/songs/${songId}/audio`, { params });
    return response.data;
  },

  async getCoverArtUrl(
    songId: number,
    params?: { revisionId?: number; expiration?: number }
  ): Promise<CoverArtUrlResponse> {
    const response = await apiClient.get(`/songs/${songId}/cover-art`, { params });
    return response.data;
  },
};
